/**
 * Polish cities data with coordinates
 */
interface PolishCity {
  name: string;
  county: string;
  lat: number;
  lon: number;
}

/**
 * Result object returned by the findNearestPolishLocation function
 */
interface NearestLocationResult {
  city: string;
  county: string;
  distance: number; // distance in kilometers
}

/**
 * List of major Polish cities with their coordinates
 */
const POLISH_CITIES: PolishCity[] = [
  { name: 'Warszawa', county: 'mazowieckie', lat: 52.2297, lon: 21.0122 },
  { name: 'Kraków', county: 'małopolskie', lat: 50.0647, lon: 19.9450 },
  { name: 'Łódź', county: 'łódzkie', lat: 51.7593, lon: 19.4559 },
  { name: 'Wrocław', county: 'dolnośląskie', lat: 51.1079, lon: 17.0385 },
  { name: 'Poznań', county: 'wielkopolskie', lat: 52.4064, lon: 16.9252 },
  { name: 'Gdańsk', county: 'pomorskie', lat: 54.3520, lon: 18.6466 },
  { name: 'Szczecin', county: 'zachodniopomorskie', lat: 53.4285, lon: 14.5528 },
  { name: 'Bydgoszcz', county: 'kujawsko-pomorskie', lat: 53.1235, lon: 18.0084 },
  { name: 'Lublin', county: 'lubelskie', lat: 51.2465, lon: 22.5684 },
  { name: 'Białystok', county: 'podlaskie', lat: 53.1325, lon: 23.1688 },
  { name: 'Katowice', county: 'śląskie', lat: 50.2649, lon: 19.0238 },
  { name: 'Gdynia', county: 'pomorskie', lat: 54.5189, lon: 18.5305 },
  { name: 'Częstochowa', county: 'śląskie', lat: 50.8118, lon: 19.1203 },
  { name: 'Radom', county: 'mazowieckie', lat: 51.4027, lon: 21.1471 },
  { name: 'Toruń', county: 'kujawsko-pomorskie', lat: 53.0138, lon: 18.5984 },
  { name: 'Kielce', county: 'świętokrzyskie', lat: 50.8661, lon: 20.6286 },
  { name: 'Rzeszów', county: 'podkarpackie', lat: 50.0412, lon: 21.9991 },
  { name: 'Olsztyn', county: 'warmińsko-mazurskie', lat: 53.7784, lon: 20.4801 },
  { name: 'Opole', county: 'opolskie', lat: 50.6751, lon: 17.9213 },
  { name: 'Gorzów Wielkopolski', county: 'lubuskie', lat: 52.7368, lon: 15.2288 },
  { name: 'Zielona Góra', county: 'lubuskie', lat: 51.9356, lon: 15.5062 },
  { name: 'Koszalin', county: 'zachodniopomorskie', lat: 54.1944, lon: 16.1722 },
  { name: 'Sopot', county: 'pomorskie', lat: 54.4400, lon: 18.5600 },
  { name: 'Płock', county: 'mazowieckie', lat: 52.5468, lon: 19.7065 },
  { name: 'Elbląg', county: 'warmińsko-mazurskie', lat: 54.1561, lon: 19.4045 },
  { name: 'Wałbrzych', county: 'dolnośląskie', lat: 50.7840, lon: 16.2843 },
  { name: 'Tarnów', county: 'małopolskie', lat: 50.0138, lon: 20.9870 },
  { name: 'Chorzów', county: 'śląskie', lat: 50.2975, lon: 18.9546 },
  { name: 'Kalisz', county: 'wielkopolskie', lat: 51.7610, lon: 18.0910 },
  { name: 'Legnica', county: 'dolnośląskie', lat: 51.2101, lon: 16.1619 },
  { name: 'Grudziądz', county: 'kujawsko-pomorskie', lat: 53.4837, lon: 18.7536 },
  { name: 'Słupsk', county: 'pomorskie', lat: 54.4641, lon: 17.0285 },
  { name: 'Jaworzno', county: 'śląskie', lat: 50.2053, lon: 19.2749 },
  { name: 'Jastrzębie-Zdrój', county: 'śląskie', lat: 49.9600, lon: 18.5833 },
  { name: 'Pruszków', county: 'mazowieckie', lat: 52.1705, lon: 20.8118 },
  { name: 'Mielec', county: 'podkarpackie', lat: 50.2871, lon: 21.4239 },
  { name: 'Ostrołęka', county: 'mazowieckie', lat: 53.0862, lon: 21.5757 },
  { name: 'Zamość', county: 'lubelskie', lat: 50.7231, lon: 23.2520 },
  { name: 'Chełm', county: 'lubelskie', lat: 51.1431, lon: 23.4712 },
  { name: 'Tomaszów Mazowiecki', county: 'łódzkie', lat: 51.5312, lon: 20.0086 },
  { name: 'Przemyśl', county: 'podkarpackie', lat: 49.7839, lon: 22.7678 },
  { name: 'Stalowa Wola', county: 'podkarpackie', lat: 50.5829, lon: 22.0536 },
  { name: 'Kędzierzyn-Koźle', county: 'opolskie', lat: 50.3498, lon: 18.2262 },
  { name: 'Inowrocław', county: 'kujawsko-pomorskie', lat: 52.7989, lon: 18.2639 },
  { name: 'Suwałki', county: 'podlaskie', lat: 54.1008, lon: 22.9309 },
  { name: 'Ełk', county: 'warmińsko-mazurskie', lat: 53.8281, lon: 22.3647 },
  { name: 'Piła', county: 'wielkopolskie', lat: 53.1515, lon: 16.7378 },
  { name: 'Luboń', county: 'wielkopolskie', lat: 52.3387, lon: 16.8927 },
  { name: 'Ostrów Wielkopolski', county: 'wielkopolskie', lat: 51.6550, lon: 17.8168 },
  { name: 'Gniezno', county: 'wielkopolskie', lat: 52.5348, lon: 17.5827 },
  { name: 'Świdnica', county: 'dolnośląskie', lat: 50.8438, lon: 16.4886 },
  { name: 'Bolesławiec', county: 'dolnośląskie', lat: 51.2624, lon: 15.5657 },
  { name: 'Nowa Sól', county: 'lubuskie', lat: 51.8033, lon: 15.7170 },
  { name: 'Malbork', county: 'pomorskie', lat: 54.0365, lon: 19.0266 },
  { name: 'Knurów', county: 'śląskie', lat: 50.2197, lon: 18.6706 },
  { name: 'Sanok', county: 'podkarpackie', lat: 49.5557, lon: 22.2056 },
  { name: 'Jarosław', county: 'podkarpackie', lat: 50.0162, lon: 22.6778 },
  { name: 'Zgierz', county: 'łódzkie', lat: 51.8551, lon: 19.4062 },
  { name: 'Skierniewice', county: 'łódzkie', lat: 51.9649, lon: 20.1584 },
  { name: 'Sieradz', county: 'łódzkie', lat: 51.5958, lon: 18.7302 },
  { name: 'Kutno', county: 'łódzkie', lat: 52.2306, lon: 19.3643 },
  { name: 'Otwock', county: 'mazowieckie', lat: 52.1059, lon: 21.2613 },
  { name: 'Piaseczno', county: 'mazowieckie', lat: 52.0814, lon: 21.0240 },
  { name: 'Ciechanów', county: 'mazowieckie', lat: 52.8815, lon: 20.6198 },
  { name: 'Mińsk Mazowiecki', county: 'mazowieckie', lat: 52.1794, lon: 21.5725 },
  { name: 'Wołomin', county: 'mazowieckie', lat: 52.3401, lon: 21.2421 },
  { name: 'Sochaczew', county: 'mazowieckie', lat: 52.2294, lon: 20.2384 },
  { name: 'Ząbki', county: 'mazowieckie', lat: 52.2927, lon: 21.1155 },
  { name: 'Mława', county: 'mazowieckie', lat: 53.1128, lon: 20.3841 },
  { name: 'Kobyłka', county: 'mazowieckie', lat: 52.3395, lon: 21.1959 },
  { name: 'Marki', county: 'mazowieckie', lat: 52.3213, lon: 21.1035 },
  { name: 'Grodzisk Mazowiecki', county: 'mazowieckie', lat: 52.1044, lon: 20.6316 },
  { name: 'Wyszków', county: 'mazowieckie', lat: 52.5928, lon: 21.4584 },
  { name: 'Bielsk Podlaski', county: 'podlaskie', lat: 52.7651, lon: 23.1865 },
  { name: 'Hajnówka', county: 'podlaskie', lat: 52.7433, lon: 23.5812 },
  { name: 'Sokółka', county: 'podlaskie', lat: 53.4072, lon: 23.5023 },
  { name: 'Augustów', county: 'podlaskie', lat: 53.8432, lon: 22.9798 },
  { name: 'Zambrów', county: 'podlaskie', lat: 52.9855, lon: 22.2432 },
  { name: 'Łomża', county: 'podlaskie', lat: 53.1781, lon: 22.0594 },
  { name: 'Grajewo', county: 'podlaskie', lat: 53.6472, lon: 22.4554 },
  { name: 'Bielawa', county: 'dolnośląskie', lat: 50.6849, lon: 16.6230 },
  { name: 'Dzierżoniów', county: 'dolnośląskie', lat: 50.7282, lon: 16.6514 },
  { name: 'Zgorzelec', county: 'dolnośląskie', lat: 51.1496, lon: 15.0084 },
  { name: 'Lubin', county: 'dolnośląskie', lat: 51.4009, lon: 16.2016 },
  { name: 'Głogów', county: 'dolnośląskie', lat: 51.6636, lon: 16.0845 },
  { name: 'Oleśnica', county: 'dolnośląskie', lat: 51.2134, lon: 17.3899 },
  { name: 'Oława', county: 'dolnośląskie', lat: 50.9466, lon: 17.2926 },
  { name: 'Kłodzko', county: 'dolnośląskie', lat: 50.4346, lon: 16.6614 },
  { name: 'Jelenia Góra', county: 'dolnośląskie', lat: 50.9040, lon: 15.7347 },
  { name: 'Chojnice', county: 'pomorskie', lat: 53.6955, lon: 17.5570 },
  { name: 'Wejherowo', county: 'pomorskie', lat: 54.6057, lon: 18.2356 },
  { name: 'Rumia', county: 'pomorskie', lat: 54.5709, lon: 18.3880 },
  { name: 'Lębork', county: 'pomorskie', lat: 54.5392, lon: 17.7501 },
  { name: 'Kołobrzeg', county: 'zachodniopomorskie', lat: 54.1756, lon: 15.5834 },
  { name: 'Świnoujście', county: 'zachodniopomorskie', lat: 53.9105, lon: 14.2471 },
  { name: 'Stargard', county: 'zachodniopomorskie', lat: 53.3367, lon: 15.0499 },
  { name: 'Police', county: 'zachodniopomorskie', lat: 53.5521, lon: 14.5718 },
  { name: 'Darłowo', county: 'zachodniopomorskie', lat: 54.4210, lon: 16.4107 },
  { name: 'Sławno', county: 'zachodniopomorskie', lat: 54.3628, lon: 16.6792 },
  { name: 'Goleniów', county: 'zachodniopomorskie', lat: 53.5639, lon: 14.8285 },
  { name: 'Gryfino', county: 'zachodniopomorskie', lat: 53.2524, lon: 14.4882 },
  { name: 'Nowogard', county: 'zachodniopomorskie', lat: 53.6744, lon: 15.1163 },
  { name: 'Świdwin', county: 'zachodniopomorskie', lat: 53.7746, lon: 15.7767 },
  { name: 'Białogard', county: 'zachodniopomorskie', lat: 54.0069, lon: 15.9875 },
  { name: 'Choszczno', county: 'zachodniopomorskie', lat: 53.1690, lon: 15.4205 },
  { name: 'Dębno', county: 'zachodniopomorskie', lat: 52.7390, lon: 14.6980 },
  { name: 'Barlinek', county: 'zachodniopomorskie', lat: 52.9946, lon: 15.2187 },
  { name: 'Wolin', county: 'zachodniopomorskie', lat: 53.8421, lon: 14.6147 },
  { name: 'Kwidzyn', county: 'pomorskie', lat: 53.7264, lon: 18.9323 },
  { name: 'Tczew', county: 'pomorskie', lat: 54.0923, lon: 18.7779 },
  { name: 'Ustka', county: 'pomorskie', lat: 54.5805, lon: 16.8619 },
  { name: 'Kartuzy', county: 'pomorskie', lat: 54.3342, lon: 18.1973 },
  { name: 'Puck', county: 'pomorskie', lat: 54.7179, lon: 18.4084 },
  { name: 'Kościerzyna', county: 'pomorskie', lat: 54.1223, lon: 17.9812 },
  { name: 'Starogard Gdański', county: 'pomorskie', lat: 53.9656, lon: 18.5163 },
  { name: 'Człuchów', county: 'pomorskie', lat: 53.6676, lon: 17.3588 },
  { name: 'Bytów', county: 'pomorskie', lat: 54.1706, lon: 17.4919 },
  { name: 'Pruszcz Gdański', county: 'pomorskie', lat: 54.2622, lon: 18.6363 },
  { name: 'Słubice', county: 'lubuskie', lat: 52.3509, lon: 14.5606 },
  { name: 'Kostrzyn nad Odrą', county: 'lubuskie', lat: 52.5871, lon: 14.6395 },
  { name: 'Żary', county: 'lubuskie', lat: 51.6420, lon: 15.1393 },
  { name: 'Żagań', county: 'lubuskie', lat: 51.6176, lon: 15.3149 },
  { name: 'Brodnica', county: 'kujawsko-pomorskie', lat: 53.2597, lon: 19.3965 },
  { name: 'Golub-Dobrzyń', county: 'kujawsko-pomorskie', lat: 53.1109, lon: 19.0538 },
  { name: 'Rypin', county: 'kujawsko-pomorskie', lat: 53.0660, lon: 19.4094 },
  { name: 'Włocławek', county: 'kujawsko-pomorskie', lat: 52.6483, lon: 19.0678 },
  { name: 'Lipno', county: 'kujawsko-pomorskie', lat: 52.8445, lon: 19.1786 },
  { name: 'Chełmno', county: 'kujawsko-pomorskie', lat: 53.3486, lon: 18.4251 },
  { name: 'Świecie', county: 'kujawsko-pomorskie', lat: 53.4095, lon: 18.4474 },
  { name: 'Nakło nad Notecią', county: 'kujawsko-pomorskie', lat: 53.1421, lon: 17.6018 },
  { name: 'Żnin', county: 'kujawsko-pomorskie', lat: 52.8496, lon: 17.7199 },
  { name: 'Tuchola', county: 'kujawsko-pomorskie', lat: 53.5879, lon: 17.8591 },
  { name: 'Konin', county: 'wielkopolskie', lat: 52.2230, lon: 18.2511 },
  { name: 'Turek', county: 'wielkopolskie', lat: 52.0155, lon: 18.5006 },
  { name: 'Jarocin', county: 'wielkopolskie', lat: 51.9727, lon: 17.5026 },
  { name: 'Września', county: 'wielkopolskie', lat: 52.3251, lon: 17.5652 },
  { name: 'Śrem', county: 'wielkopolskie', lat: 52.0887, lon: 17.0151 },
  { name: 'Krotoszyn', county: 'wielkopolskie', lat: 51.6987, lon: 17.4374 },
  { name: 'Gostyń', county: 'wielkopolskie', lat: 51.8825, lon: 17.0123 },
  { name: 'Rawicz', county: 'wielkopolskie', lat: 51.6095, lon: 16.8585 },
  { name: 'Koło', county: 'wielkopolskie', lat: 52.2002, lon: 18.6385 },
  { name: 'Leszno', county: 'wielkopolskie', lat: 51.8403, lon: 16.5749 },
  { name: 'Brzeg', county: 'opolskie', lat: 50.8608, lon: 17.4668 },
  { name: 'Nysa', county: 'opolskie', lat: 50.4738, lon: 17.3344 },
  { name: 'Kluczbork', county: 'opolskie', lat: 50.9728, lon: 18.2182 },
  { name: 'Prudnik', county: 'opolskie', lat: 50.3212, lon: 17.5755 },
  { name: 'Głubczyce', county: 'opolskie', lat: 50.2009, lon: 17.8286 },
  { name: 'Namysłów', county: 'opolskie', lat: 51.0759, lon: 17.7228 },
  { name: 'Strzelce Opolskie', county: 'opolskie', lat: 50.5107, lon: 18.3006 },
  { name: 'Olesno', county: 'opolskie', lat: 50.8765, lon: 18.4212 },
  { name: 'Krapkowice', county: 'opolskie', lat: 50.4750, lon: 17.9654 },
  { name: 'Grodków', county: 'opolskie', lat: 50.6984, lon: 17.3845 },
  { name: 'Nowy Sącz', county: 'małopolskie', lat: 49.6218, lon: 20.6971 },
  { name: 'Gorlice', county: 'małopolskie', lat: 49.6556, lon: 21.1604 },
  { name: 'Zakopane', county: 'małopolskie', lat: 49.2992, lon: 19.9496 },
  { name: 'Bochnia', county: 'małopolskie', lat: 49.9690, lon: 20.4303 },
  { name: 'Olkusz', county: 'małopolskie', lat: 50.2813, lon: 19.5650 },
  { name: 'Chrzanów', county: 'małopolskie', lat: 50.1355, lon: 19.4020 },
  { name: 'Wieliczka', county: 'małopolskie', lat: 49.9879, lon: 20.0647 },
  { name: 'Wadowice', county: 'małopolskie', lat: 49.8834, lon: 19.4929 },
  { name: 'Myślenice', county: 'małopolskie', lat: 49.8337, lon: 19.9383 },
  { name: 'Limanowa', county: 'małopolskie', lat: 49.7059, lon: 20.4219 },
  { name: 'Piotrków Trybunalski', county: 'łódzkie', lat: 51.4052, lon: 19.7030 },
  { name: 'Bełchatów', county: 'łódzkie', lat: 51.3688, lon: 19.3567 },
  { name: 'Pabianice', county: 'łódzkie', lat: 51.6645, lon: 19.3547 },
  { name: 'Zduńska Wola', county: 'łódzkie', lat: 51.5992, lon: 18.9397 },
  { name: 'Wieluń', county: 'łódzkie', lat: 51.2210, lon: 18.5696 },
  { name: 'Łowicz', county: 'łódzkie', lat: 52.1071, lon: 19.9453 },
  { name: 'Łęczyca', county: 'łódzkie', lat: 52.0596, lon: 19.1997 },
  { name: 'Opoczno', county: 'łódzkie', lat: 51.3757, lon: 20.2783 },
  { name: 'Rawa Mazowiecka', county: 'łódzkie', lat: 51.7658, lon: 20.2549 },
  { name: 'Brzeziny', county: 'łódzkie', lat: 51.8002, lon: 19.7514 },
  { name: 'Gliwice', county: 'śląskie', lat: 50.2945, lon: 18.6714 },
  { name: 'Zabrze', county: 'śląskie', lat: 50.3249, lon: 18.7857 },
  { name: 'Bytom', county: 'śląskie', lat: 50.3480, lon: 18.9328 },
  { name: 'Ruda Śląska', county: 'śląskie', lat: 50.2559, lon: 18.8553 },
  { name: 'Tychy', county: 'śląskie', lat: 50.1372, lon: 18.9665 },
  { name: 'Dąbrowa Górnicza', county: 'śląskie', lat: 50.3181, lon: 19.2374 },
  { name: 'Sosnowiec', county: 'śląskie', lat: 50.2863, lon: 19.1041 },
  { name: 'Mysłowice', county: 'śląskie', lat: 50.2078, lon: 19.1667 },
  { name: 'Siemianowice Śląskie', county: 'śląskie', lat: 50.3266, lon: 19.0290 },
  { name: 'Piekary Śląskie', county: 'śląskie', lat: 50.3802, lon: 18.9265 },
  { name: 'Legionowo', county: 'mazowieckie', lat: 52.4013, lon: 20.9266 },
  { name: 'Siedlce', county: 'mazowieckie', lat: 52.1677, lon: 22.2901 },
  { name: 'Żyrardów', county: 'mazowieckie', lat: 52.0488, lon: 20.4460 },
  { name: 'Płońsk', county: 'mazowieckie', lat: 52.6235, lon: 20.3755 },
  { name: 'Kozienice', county: 'mazowieckie', lat: 51.5829, lon: 21.5478 },
  { name: 'Garwolin', county: 'mazowieckie', lat: 51.8975, lon: 21.6147 },
  { name: 'Sierpc', county: 'mazowieckie', lat: 52.8568, lon: 19.6691 },
  { name: 'Gostynin', county: 'mazowieckie', lat: 52.4294, lon: 19.4619 },
  { name: 'Pułtusk', county: 'mazowieckie', lat: 52.7025, lon: 21.0826 },
  { name: 'Przasnysz', county: 'mazowieckie', lat: 53.0190, lon: 20.8803 },
  { name: 'Krosno', county: 'podkarpackie', lat: 49.6825, lon: 21.7667 },
  { name: 'Dębica', county: 'podkarpackie', lat: 50.0515, lon: 21.4115 },
  { name: 'Łańcut', county: 'podkarpackie', lat: 50.0687, lon: 22.2291 },
  { name: 'Leżajsk', county: 'podkarpackie', lat: 50.2626, lon: 22.4193 },
  { name: 'Nisko', county: 'podkarpackie', lat: 50.5199, lon: 22.1397 },
  { name: 'Lubaczów', county: 'podkarpackie', lat: 50.1570, lon: 23.1234 },
  { name: 'Kolbuszowa', county: 'podkarpackie', lat: 50.2441, lon: 21.7761 },
  { name: 'Strzyżów', county: 'podkarpackie', lat: 49.8707, lon: 21.7941 },
  { name: 'Brzozów', county: 'podkarpackie', lat: 49.6950, lon: 22.0193 },
  { name: 'Ropczyce', county: 'podkarpackie', lat: 50.0523, lon: 21.6089 },
  { name: 'Głogów Małopolski', county: 'podkarpackie', lat: 50.1517, lon: 21.9638 },
  { name: 'Dynów', county: 'podkarpackie', lat: 49.8151, lon: 22.2339 },
  { name: 'Ustrzyki Dolne', county: 'podkarpackie', lat: 49.4304, lon: 22.5936 },
  { name: 'Lesko', county: 'podkarpackie', lat: 49.4705, lon: 22.3304 },
  { name: 'Giżycko', county: 'warmińsko-mazurskie', lat: 54.0381, lon: 21.7644 },
  { name: 'Iława', county: 'warmińsko-mazurskie', lat: 53.5960, lon: 19.5685 },
  { name: 'Kętrzyn', county: 'warmińsko-mazurskie', lat: 54.0768, lon: 21.3753 },
  { name: 'Bartoszyce', county: 'warmińsko-mazurskie', lat: 54.2535, lon: 20.8081 },
  { name: 'Szczytno', county: 'warmińsko-mazurskie', lat: 53.5626, lon: 20.9855 },
  { name: 'Mrągowo', county: 'warmińsko-mazurskie', lat: 53.8644, lon: 21.3051 },
  { name: 'Lidzbark Warmiński', county: 'warmińsko-mazurskie', lat: 54.1259, lon: 20.5795 },
  { name: 'Działdowo', county: 'warmińsko-mazurskie', lat: 53.2396, lon: 20.1700 },
  { name: 'Sandomierz', county: 'świętokrzyskie', lat: 50.6826, lon: 21.7490 },
  { name: 'Ostrowiec Świętokrzyski', county: 'świętokrzyskie', lat: 50.9294, lon: 21.3853 },
  { name: 'Starachowice', county: 'świętokrzyskie', lat: 51.0374, lon: 21.0713 },
  { name: 'Skarżysko-Kamienna', county: 'świętokrzyskie', lat: 51.1131, lon: 20.8716 },
  { name: 'Busko-Zdrój', county: 'świętokrzyskie', lat: 50.4708, lon: 20.7190 },
  { name: 'Jędrzejów', county: 'świętokrzyskie', lat: 50.6394, lon: 20.3045 },
  { name: 'Staszów', county: 'świętokrzyskie', lat: 50.5631, lon: 21.1659 },
  { name: 'Końskie', county: 'świętokrzyskie', lat: 51.1917, lon: 20.4061 },
  { name: 'Włoszczowa', county: 'świętokrzyskie', lat: 50.8526, lon: 19.9659 },
  { name: 'Pińczów', county: 'świętokrzyskie', lat: 50.5205, lon: 20.5265 },
  { name: 'Kazimierza Wielka', county: 'świętokrzyskie', lat: 50.2656, lon: 20.4936 },
  { name: 'Opatów', county: 'świętokrzyskie', lat: 50.8005, lon: 21.4250 },
  { name: 'Połaniec', county: 'świętokrzyskie', lat: 50.4332, lon: 21.2809 },
  { name: 'Chęciny', county: 'świętokrzyskie', lat: 50.8002, lon: 20.4623 },
  { name: 'Ćmielów', county: 'świętokrzyskie', lat: 50.8903, lon: 21.5142 },
  { name: 'Białobrzegi', county: 'mazowieckie', lat: 51.6469, lon: 20.9504 },
  { name: 'Grójec', county: 'mazowieckie', lat: 51.8625, lon: 20.8676 },
  { name: 'Mogielnica', county: 'mazowieckie', lat: 51.6893, lon: 20.7223 },
  { name: 'Warka', county: 'mazowieckie', lat: 51.7841, lon: 21.1909 },
  { name: 'Tarczyn', county: 'mazowieckie', lat: 51.9819, lon: 20.8339 },
  { name: 'Żuromin', county: 'mazowieckie', lat: 53.0661, lon: 19.9089 },
  { name: 'Bieżuń', county: 'mazowieckie', lat: 52.9611, lon: 19.8898 },
  { name: 'Raciąż', county: 'mazowieckie', lat: 52.7817, lon: 20.1157 },
  { name: 'Glinojeck', county: 'mazowieckie', lat: 52.8201, lon: 20.2921 },
  { name: 'Drobin', county: 'mazowieckie', lat: 52.7378, lon: 19.9892 },
  { name: 'Nasielsk', county: 'mazowieckie', lat: 52.5890, lon: 20.8053 },
  { name: 'Zakroczym', county: 'mazowieckie', lat: 52.4337, lon: 20.6117 },
  { name: 'Wyszogród', county: 'mazowieckie', lat: 52.3898, lon: 20.1908 },
  { name: 'Lipsko', county: 'mazowieckie', lat: 51.1596, lon: 21.6493 },
  { name: 'Iłża', county: 'mazowieckie', lat: 51.1637, lon: 21.2398 },
  { name: 'Świercze', county: 'mazowieckie', lat: 52.6703, lon: 20.7651 },
  { name: 'Krynki', county: 'podlaskie', lat: 53.2647, lon: 23.7746 },
  { name: 'Siemiatycze', county: 'podlaskie', lat: 52.4272, lon: 22.8623 },
  { name: 'Drohiczyn', county: 'podlaskie', lat: 52.4001, lon: 22.6586 },
  { name: 'Brańsk', county: 'podlaskie', lat: 52.7444, lon: 22.8377 },
  { name: 'Ciechanowiec', county: 'podlaskie', lat: 52.6783, lon: 22.4981 },
  { name: 'Wysokie Mazowieckie', county: 'podlaskie', lat: 52.9166, lon: 22.5171 },
  { name: 'Czyżew', county: 'podlaskie', lat: 52.7969, lon: 22.3124 },
  { name: 'Szepietowo', county: 'podlaskie', lat: 52.8655, lon: 22.5441 },
  { name: 'Knyszyn', county: 'podlaskie', lat: 53.3140, lon: 22.9196 },
  { name: 'Mońki', county: 'podlaskie', lat: 53.4050, lon: 22.7992 },
  { name: 'Goniądz', county: 'podlaskie', lat: 53.4894, lon: 22.7358 },
  { name: 'Sejny', county: 'podlaskie', lat: 54.1080, lon: 23.3471 },
  { name: 'Dąbrowa Białostocka', county: 'podlaskie', lat: 53.6539, lon: 23.3479 },
  { name: 'Lipsk', county: 'podlaskie', lat: 53.7333, lon: 23.4024 },
  { name: 'Choroszcz', county: 'podlaskie', lat: 53.1433, lon: 22.9889 },
  { name: 'Supraśl', county: 'podlaskie', lat: 53.2053, lon: 23.3393 },
  { name: 'Wasilków', county: 'podlaskie', lat: 53.1991, lon: 23.2078 },
  { name: 'Łapy', county: 'podlaskie', lat: 52.9911, lon: 22.8844 },
  { name: 'Czarna Białostocka', county: 'podlaskie', lat: 53.3051, lon: 23.2815 },
  { name: 'Suraż', county: 'podlaskie', lat: 52.9492, lon: 22.9564 },
  { name: 'Tykocin', county: 'podlaskie', lat: 53.2056, lon: 22.7746 },
  { name: 'Nowogród', county: 'podlaskie', lat: 53.2270, lon: 21.8821 },
  { name: 'Jedwabne', county: 'podlaskie', lat: 53.2855, lon: 22.3035 },
  { name: 'Stawiski', county: 'podlaskie', lat: 53.3799, lon: 22.1547 },
  { name: 'Kleszczele', county: 'podlaskie', lat: 52.5731, lon: 23.3253 },
  { name: 'Krzeszowice', county: 'małopolskie', lat: 50.1425, lon: 19.6323 },
  { name: 'Skała', county: 'małopolskie', lat: 50.2305, lon: 19.8534 },
  { name: 'Proszowice', county: 'małopolskie', lat: 50.1925, lon: 20.2891 },
  { name: 'Niepołomice', county: 'małopolskie', lat: 50.0344, lon: 20.2226 },
  { name: 'Mszana Dolna', county: 'małopolskie', lat: 49.6743, lon: 20.0799 },
  { name: 'Rabka-Zdrój', county: 'małopolskie', lat: 49.6089, lon: 19.9665 },
  { name: 'Sucha Beskidzka', county: 'małopolskie', lat: 49.7419, lon: 19.5948 },
  { name: 'Jordanów', county: 'małopolskie', lat: 49.6494, lon: 19.8298 },
  { name: 'Dobczyce', county: 'małopolskie', lat: 49.8811, lon: 20.0894 },
  { name: 'Kalwaria Zebrzydowska', county: 'małopolskie', lat: 49.8676, lon: 19.6773 },
  { name: 'Trzebinia', county: 'małopolskie', lat: 50.1593, lon: 19.4697 },
  { name: 'Libiąż', county: 'małopolskie', lat: 50.1039, lon: 19.3157 },
  { name: 'Andrychów', county: 'małopolskie', lat: 49.8550, lon: 19.3413 },
  { name: 'Kęty', county: 'małopolskie', lat: 49.8821, lon: 19.2233 },
  { name: 'Maków Podhalański', county: 'małopolskie', lat: 49.7301, lon: 19.6776 },
  { name: 'Szczawnica', county: 'małopolskie', lat: 49.4244, lon: 20.4849 },
  { name: 'Piwniczna-Zdrój', county: 'małopolskie', lat: 49.4406, lon: 20.7143 },
  { name: 'Krynica-Zdrój', county: 'małopolskie', lat: 49.4215, lon: 20.9594 },
  { name: 'Muszyna', county: 'małopolskie', lat: 49.3566, lon: 20.8972 },
  { name: 'Stary Sącz', county: 'małopolskie', lat: 49.5636, lon: 20.6350 },
  { name: 'Grybów', county: 'małopolskie', lat: 49.6244, lon: 20.9479 },
  { name: 'Tuchów', county: 'małopolskie', lat: 49.8948, lon: 21.0541 },
  { name: 'Ciężkowice', county: 'małopolskie', lat: 49.7857, lon: 20.9733 },
  { name: 'Zakliczyn', county: 'małopolskie', lat: 49.8560, lon: 20.8093 },
  { name: 'Biecz', county: 'małopolskie', lat: 49.7358, lon: 21.2632 },
  { name: 'Wojnicz', county: 'małopolskie', lat: 49.9582, lon: 20.8377 },
  { name: 'Radłów', county: 'małopolskie', lat: 50.0841, lon: 20.8496 },
  { name: 'Żabno', county: 'małopolskie', lat: 50.1333, lon: 20.8861 },
  { name: 'Działoszyce', county: 'małopolskie', lat: 50.3651, lon: 20.3519 },
  { name: 'Słomniki', county: 'małopolskie', lat: 50.2401, lon: 20.0822 },
  { name: 'Wolbrom', county: 'małopolskie', lat: 50.3795, lon: 19.7581 },
  { name: 'Przysucha', county: 'mazowieckie', lat: 51.3587, lon: 20.6289 },
  { name: 'Szydłowiec', county: 'mazowieckie', lat: 51.2281, lon: 20.8611 },
  { name: 'Zwoleń', county: 'mazowieckie', lat: 51.3554, lon: 21.5877 },
  { name: 'Łosice', county: 'mazowieckie', lat: 52.2113, lon: 22.7180 },
  { name: 'Mordy', county: 'mazowieckie', lat: 52.2116, lon: 22.5172 },
  { name: 'Łaskarzew', county: 'mazowieckie', lat: 51.7898, lon: 21.5913 },
  { name: 'Pilawa', county: 'mazowieckie', lat: 51.9595, lon: 21.5307 },
  { name: 'Żelechów', county: 'mazowieckie', lat: 51.8105, lon: 21.8972 },
  { name: 'Skaryszew', county: 'mazowieckie', lat: 51.3107, lon: 21.2523 },
  { name: 'Pionki', county: 'mazowieckie', lat: 51.4760, lon: 21.4500 },
  { name: 'Wyśmierzyce', county: 'mazowieckie', lat: 51.6248, lon: 20.9376 },
  { name: 'Barcin', county: 'kujawsko-pomorskie', lat: 52.8666, lon: 17.9463 },
  { name: 'Gniew', county: 'pomorskie', lat: 53.8359, lon: 18.8231 },
  { name: 'Koronowo', county: 'kujawsko-pomorskie', lat: 53.3137, lon: 17.9370 },
  { name: 'Mogilno', county: 'kujawsko-pomorskie', lat: 52.6581, lon: 17.9558 },
  { name: 'Sępólno Krajeńskie', county: 'kujawsko-pomorskie', lat: 53.4519, lon: 17.5317 },
  { name: 'Szubin', county: 'kujawsko-pomorskie', lat: 53.0097, lon: 17.7400 },
  { name: 'Wąbrzeźno', county: 'kujawsko-pomorskie', lat: 53.2799, lon: 18.9477 },
  { name: 'Łasin', county: 'kujawsko-pomorskie', lat: 53.5178, lon: 19.0884 },
  { name: 'Radziejów', county: 'kujawsko-pomorskie', lat: 52.6248, lon: 18.5277 },
  { name: 'Kcynia', county: 'kujawsko-pomorskie', lat: 52.9919, lon: 17.4884 },
  { name: 'Strzelno', county: 'kujawsko-pomorskie', lat: 52.6279, lon: 18.1725 },
  { name: 'Piotrków Kujawski', county: 'kujawsko-pomorskie', lat: 52.5512, lon: 18.4991 },
  { name: 'Kowal', county: 'kujawsko-pomorskie', lat: 52.5305, lon: 19.1477 },
  { name: 'Kruszwica', county: 'kujawsko-pomorskie', lat: 52.6756, lon: 18.3314 },
  { name: 'Janikowo', county: 'kujawsko-pomorskie', lat: 52.7533, lon: 18.1133 },
  { name: 'Więcbork', county: 'kujawsko-pomorskie', lat: 53.3538, lon: 17.4906 },
  { name: 'Kamień Krajeński', county: 'kujawsko-pomorskie', lat: 53.5335, lon: 17.5209 },
  { name: 'Nowe', county: 'kujawsko-pomorskie', lat: 53.6491, lon: 18.7272 },
  { name: 'Skępe', county: 'kujawsko-pomorskie', lat: 52.8680, lon: 19.3561 },
  { name: 'Lubraniec', county: 'kujawsko-pomorskie', lat: 52.5436, lon: 18.8387 },
  { name: 'Chodecz', county: 'kujawsko-pomorskie', lat: 52.4051, lon: 19.0276 },
  { name: 'Brześć Kujawski', county: 'kujawsko-pomorskie', lat: 52.6053, lon: 18.8999 },
  { name: 'Izbica Kujawska', county: 'kujawsko-pomorskie', lat: 52.4209, lon: 18.7629 },
  { name: 'Ciechocinek', county: 'kujawsko-pomorskie', lat: 52.8791, lon: 18.7950 },
  { name: 'Solec Kujawski', county: 'kujawsko-pomorskie', lat: 53.0837, lon: 18.2257 },
  { name: 'Nieszawa', county: 'kujawsko-pomorskie', lat: 52.8345, lon: 18.8992 },
  { name: 'Władysławowo', county: 'pomorskie', lat: 54.7909, lon: 18.4009 },
  { name: 'Reda', county: 'pomorskie', lat: 54.6053, lon: 18.3472 },
  { name: 'Hel', county: 'pomorskie', lat: 54.6081, lon: 18.8087 },
  { name: 'Jastarnia', county: 'pomorskie', lat: 54.6982, lon: 18.6783 },
  { name: 'Krynica Morska', county: 'pomorskie', lat: 54.3805, lon: 19.4440 },
  { name: 'Łeba', county: 'pomorskie', lat: 54.7609, lon: 17.5550 },
  { name: 'Dzierzgoń', county: 'pomorskie', lat: 53.9218, lon: 19.3470 },
  { name: 'Nowy Dwór Gdański', county: 'pomorskie', lat: 54.2130, lon: 19.1177 },
  { name: 'Nowy Staw', county: 'pomorskie', lat: 54.1361, lon: 19.0093 },
  { name: 'Pelplin', county: 'pomorskie', lat: 53.9283, lon: 18.6977 },
  { name: 'Skórcz', county: 'pomorskie', lat: 53.7966, lon: 18.5259 },
  { name: 'Żukowo', county: 'pomorskie', lat: 54.3422, lon: 18.3648 },
  { name: 'Czersk', county: 'pomorskie', lat: 53.7959, lon: 17.9765 },
  { name: 'Brusy', county: 'pomorskie', lat: 53.8845, lon: 17.7234 },
  { name: 'Debrzno', county: 'pomorskie', lat: 53.5381, lon: 17.2364 },
  { name: 'Miastko', county: 'pomorskie', lat: 54.0028, lon: 16.9757 },
  { name: 'Sztum', county: 'pomorskie', lat: 53.9207, lon: 19.0307 },
  { name: 'Gardeja', county: 'pomorskie', lat: 53.6115, lon: 18.9467 },
  { name: 'Kępice', county: 'pomorskie', lat: 54.2411, lon: 16.8897 },
  { name: 'Łęczyce', county: 'pomorskie', lat: 54.5940, lon: 17.8619 },
  { name: 'Liniewo', county: 'pomorskie', lat: 54.0767, lon: 18.2266 },
  { name: 'Luzino', county: 'pomorskie', lat: 54.5660, lon: 18.1089 },
  { name: 'Stężyca', county: 'pomorskie', lat: 54.2059, lon: 17.9557 },
  { name: 'Sulęczyno', county: 'pomorskie', lat: 54.2329, lon: 17.7735 },
  { name: 'Trąbczyn', county: 'pomorskie', lat: 54.2957, lon: 18.0287 },
  { name: 'Świebodzin', county: 'lubuskie', lat: 52.2475, lon: 15.5336 },
  { name: 'Kożuchów', county: 'lubuskie', lat: 51.7456, lon: 15.5949 },
  { name: 'Szprotawa', county: 'lubuskie', lat: 51.5656, lon: 15.5366 },
  { name: 'Wschowa', county: 'lubuskie', lat: 51.8071, lon: 16.3166 },
  { name: 'Krosno Odrzańskie', county: 'lubuskie', lat: 52.0549, lon: 15.0988 },
  { name: 'Sulechów', county: 'lubuskie', lat: 52.0836, lon: 15.6251 },
  { name: 'Gubin', county: 'lubuskie', lat: 51.9496, lon: 14.7284 },
  { name: 'Lubsko', county: 'lubuskie', lat: 51.7847, lon: 14.9720 },
  { name: 'Rzepin', county: 'lubuskie', lat: 52.3464, lon: 14.8323 },
  { name: 'Skwierzyna', county: 'lubuskie', lat: 52.5991, lon: 15.5065 },
  { name: 'Międzyrzecz', county: 'lubuskie', lat: 52.4446, lon: 15.5780 },
  { name: 'Strzelce Krajeńskie', county: 'lubuskie', lat: 52.8751, lon: 15.5298 },
  { name: 'Drezdenko', county: 'lubuskie', lat: 52.8383, lon: 15.8308 },
  { name: 'Witnica', county: 'lubuskie', lat: 52.6732, lon: 14.8977 },
  { name: 'Bytnica', county: 'lubuskie', lat: 52.1478, lon: 15.1694 },
  { name: 'Cybinka', county: 'lubuskie', lat: 52.1945, lon: 14.7957 },
  { name: 'Dobiegniew', county: 'lubuskie', lat: 52.9695, lon: 15.7536 },
  { name: 'Iłowa', county: 'lubuskie', lat: 51.5006, lon: 15.1998 },
  { name: 'Kargowa', county: 'lubuskie', lat: 52.0663, lon: 15.8613 },
  { name: 'Małomice', county: 'lubuskie', lat: 51.5559, lon: 15.4497 },
  { name: 'Nowa Wieś', county: 'lubuskie', lat: 52.1779, lon: 15.5648 },
  { name: 'Ośno Lubuskie', county: 'lubuskie', lat: 52.4536, lon: 14.8752 },
  { name: 'Przewóz', county: 'lubuskie', lat: 51.4805, lon: 14.9519 },
  { name: 'Sulęcin', county: 'lubuskie', lat: 52.4443, lon: 15.1168 },
  { name: 'Torzym', county: 'lubuskie', lat: 52.3133, lon: 15.0825 },
  { name: 'Trzciel', county: 'lubuskie', lat: 52.3650, lon: 15.8730 },
  { name: 'Zbąszynek', county: 'lubuskie', lat: 52.2432, lon: 15.8165 },
  { name: 'Babimost', county: 'lubuskie', lat: 52.1649, lon: 15.8287 },
  { name: 'Bytom Odrzański', county: 'lubuskie', lat: 51.7356, lon: 15.8236 },
  { name: 'Czerwieńsk', county: 'lubuskie', lat: 52.0129, lon: 15.4013 },
  { name: 'Gozdnica', county: 'lubuskie', lat: 51.4363, lon: 15.0986 },
  { name: 'Jasień', county: 'lubuskie', lat: 51.7516, lon: 15.0140 },
  { name: 'Lubniewice', county: 'lubuskie', lat: 52.5164, lon: 15.2501 },
  { name: 'Nowogród Bobrzański', county: 'lubuskie', lat: 51.7987, lon: 15.2213 },
  { name: 'Trzebiechów', county: 'lubuskie', lat: 52.0211, lon: 15.7357 },
  { name: 'Zabór', county: 'lubuskie', lat: 51.9519, lon: 15.7173 },
  { name: 'Boguszów-Gorce', county: 'dolnośląskie', lat: 50.7551, lon: 16.2049 },
  { name: 'Kamienna Góra', county: 'dolnośląskie', lat: 50.7831, lon: 16.0304 },
  { name: 'Kowary', county: 'dolnośląskie', lat: 50.7913, lon: 15.8356 },
  { name: 'Lubań', county: 'dolnośląskie', lat: 51.1201, lon: 15.2877 },
  { name: 'Lwówek Śląski', county: 'dolnośląskie', lat: 51.1107, lon: 15.5859 },
  { name: 'Strzegom', county: 'dolnośląskie', lat: 50.9626, lon: 16.3501 },
  { name: 'Złotoryja', county: 'dolnośląskie', lat: 51.1264, lon: 15.9198 },
  { name: 'Gryfów Śląski', county: 'dolnośląskie', lat: 51.0308, lon: 15.4202 },
  { name: 'Mirsk', county: 'dolnośląskie', lat: 50.9705, lon: 15.3857 },
  { name: 'Nowogrodziec', county: 'dolnośląskie', lat: 51.2016, lon: 15.3985 },
  { name: 'Polkowice', county: 'dolnośląskie', lat: 51.5027, lon: 16.0726 },
  { name: 'Prusice', county: 'dolnośląskie', lat: 51.3702, lon: 16.9603 },
];

/**
 * Calculate the Haversine distance between two points on Earth
 * @param lat1 - Latitude of first point in degrees
 * @param lon1 - Longitude of first point in degrees
 * @param lat2 - Latitude of second point in degrees
 * @param lon2 - Longitude of second point in degrees
 * @returns Distance in kilometers
 */
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  // Convert latitude and longitude from degrees to radians
  const toRadians = (degrees: number) => degrees * Math.PI / 180;

  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  lat1 = toRadians(lat1);
  lat2 = toRadians(lat2);

  // Haversine formula
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  // Earth's radius in kilometers
  const radius = 6371;
  return radius * c;
}

/**
 * Find the nearest Polish city and county to the given coordinates
 * @param coordinates - Array containing [latitude, longitude]
 * @returns Object with nearest city, county and distance in kilometers
 */
export function findNearestPolishLocation(coordinates: [number, number]): NearestLocationResult {
  if (!coordinates || coordinates.length !== 2) {
    throw new Error('Invalid coordinates. Expected [latitude, longitude] array.');
  }

  const [latitude, longitude] = coordinates;

  let nearestCity: PolishCity = POLISH_CITIES[0];
  let minDistance: number = Number.MAX_VALUE;

  // Find the nearest city
  for (const city of POLISH_CITIES) {
    const distance = haversineDistance(latitude, longitude, city.lat, city.lon);
    if (distance < minDistance) {
      minDistance = distance;
      nearestCity = city;
    }
  }

  return {
    city: nearestCity.name,
    county: nearestCity.county,
    distance: Math.round(minDistance * 10) / 10 // Round to 1 decimal place
  };
}

/**
 * Format the result into a human-readable string
 * @param coordinates - Array containing [latitude, longitude]
 * @param type - Optional parameter to specify what to return: 'city', 'county', or both (default)
 * @returns Formatted string like "Kraków, małopolskie (12.3 km)" or just the city or county
 */
export function formatNearestLocation(coordinates: [number, number], type?: 'city' | 'county'): string {
  const result = findNearestPolishLocation(coordinates);

  if (type === 'city') {
    return result.city;
  } else if (type === 'county') {
    return result.county;
  } else {
    return `${result.city}, ${result.county} (~${result.distance} km)`;
  }
}

/**
 * Parse coordinates from various formats and find the nearest Polish location
 * @param coordinates - Can be a [lat, lng] array, a "lat,lng" string, or a {lat, lng} object
 * @param type - Optional parameter to specify what to return: 'city', 'county', or both (default)
 * @returns The nearest Polish location as a formatted string or error message
 */
export function parseCoordinatesAndFindLocation(coordinates: any, type?: 'city' | 'county'): string {
  try {
    // Handle array format [lat, lng]
    if (Array.isArray(coordinates) && coordinates.length === 2) {
      const lat = parseFloat(String(coordinates[0]));
      const lng = parseFloat(String(coordinates[1]));

      if (!isNaN(lat) && !isNaN(lng)) {
        return formatNearestLocation([lat, lng], type);
      }
    }

    // Handle string format "lat,lng"
    if (typeof coordinates === 'string') {
      const parts = coordinates.split(',').map(part => parseFloat(part.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return formatNearestLocation([parts[0], parts[1]], type);
      }
    }

    // Handle object format {lat, lng} or {latitude, longitude}
    if (typeof coordinates === 'object' && coordinates !== null) {
      const lat = coordinates.lat || coordinates.latitude;
      const lng = coordinates.lng || coordinates.lon || coordinates.longitude;

      if (typeof lat === 'number' && typeof lng === 'number') {
        return formatNearestLocation([lat, lng], type);
      }
    }

    // If we couldn't parse the coordinates, return the original value as a string
    return typeof coordinates === 'string' ? coordinates : 'Lokalizacja nieznana';
  } catch (error) {
    console.error('Error parsing coordinates:', error);
    return 'Lokalizacja nieznana';
  }
}